import { ApiUniversity } from '@repo/types';
import { cn } from '@repo/ui/utils';
import { useGetUserUniversities } from '@src/api/useUniversities';
import { useUniSelectorContext } from '@src/contexts/uni-selector/useUniSelectorContext';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DeleteUniversityDialog } from './DeleteUniversityDialog';
import { useHasPermission } from '@src/hooks/useHasPermission';
import { FormAutocompleteSelectField } from './FormAutocompleteSelectField';

export interface UniSelectorProps {
  className?: string;
}

export function UniSelector({ className }: UniSelectorProps) {
  const { selectedUniversityId, setSelectedUniversityId } =
    useUniSelectorContext();
  const universityRef = useRef<HTMLInputElement>(null);
  const [universityText, setUniversityText] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUniversity, setSelectedUniversity] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const { hasPermission } = useHasPermission();

  const { data: universities } = useGetUserUniversities({
    includeManagedUniversities: true,
  });

  useEffect(() => {
    if (selectedUniversityId) {
      const selectedUniversity = universities?.find(
        (university) => university.id === selectedUniversityId
      );
      if (selectedUniversity) {
        setSelectedUniversity({
          id: selectedUniversity.id,
          name: selectedUniversity.name,
        });
        setUniversityText(selectedUniversity.name);
      }
    }
  }, [selectedUniversityId, universities]);

  const filteredUniversities = useMemo(() => {
    if (!isEditing || universityText.trim() === '') return universities;
    return universities?.filter((university) =>
      university.name.toLowerCase().includes(universityText.toLowerCase())
    );
  }, [universities, universityText, isEditing]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const handleUniversitySelect = (university: ApiUniversity) => {
    setUniversityText(university.name);
    setSelectedUniversity({ id: university.id, name: university.name });
    setSelectedUniversityId(university.id);
    setIsEditing(false);
  };

  const handleUniversityDeleted = () => {
    setSelectedUniversity(null);
    setUniversityText('');
    setSelectedUniversityId(null);
  };

  const handleSelectedUniversityChange = (universityName: string) => {
    setIsEditing(true);
    setUniversityText(universityName);
    const selectedUniversity = universities?.find(
      (university) => university.name === universityName
    );
    setSelectedUniversity(
      selectedUniversity
        ? { id: selectedUniversity.id, name: selectedUniversity.name }
        : null
    );
  };

  const handleSelectedUniversityBlur = () => {
    if (selectedUniversity) {
      setSelectedUniversityId(selectedUniversity.id);
    } else {
      setSelectedUniversityId(null);
    }
  };

  if (!universities || universities.length === 1) {
    return null;
  }

  const options = (filteredUniversities ?? []).map((university) => ({
    key: university.id,
    label: university.name,
    onSelect: () => handleUniversitySelect(university),
    className: 'text-foreground autocomplete-option',
  }));

  return (
    <>
      <FormAutocompleteSelectField
        ref={universityRef}
        label="University"
        id="university-selector"
        labelClassName="sr-only"
        onKeyDown={handleKeyDown}
        onChange={handleSelectedUniversityChange}
        onBlur={handleSelectedUniversityBlur}
        value={universityText}
        fieldClassName={cn('w-full', 'max-w-md', className)}
        className="uni-selector"
        options={options}
      />
      {selectedUniversity && hasPermission('delete-university') && (
        <DeleteUniversityDialog
          universityName={selectedUniversity.name}
          universityId={selectedUniversity.id}
          onDeleted={handleUniversityDeleted}
        />
      )}
    </>
  );
}
